import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';

type VobizConnectionStatus = 'offline' | 'connecting' | 'registered' | 'incoming' | 'calling' | 'in_call' | 'error';

interface SoftphoneProps {
  agentId: string;
}

interface StoredCredentials {
  username: string;
  password: string;
}

interface VobizClient {
  on: (event: string, handler: (...args: any[]) => void) => void;
  login: (username: string, password: string) => void;
  logout: () => void;
  answer: () => void;
  reject: () => void;
  hangup: () => void;
  mute: () => void;
  unmute: () => void;
  remoteView?: HTMLAudioElement | HTMLVideoElement;
  getPeerConnection?: () => { pc?: RTCPeerConnection };
}

interface VobizInstance {
  client: VobizClient;
}

declare global {
  interface Window {
    Vobiz?: new (options: Record<string, unknown>) => VobizInstance;
  }
}

const SDK_SRC = 'https://cdn.jsdelivr.net/npm/vobiz-webrtc-sdk';
const CREDENTIALS: StoredCredentials = {
  username: 'santaanagent239935696199967',
  password: 'Santaan@321',
};

const getStatusLabel = (status: VobizConnectionStatus) => {
  switch (status) {
    case 'connecting':
      return 'Connecting';
    case 'registered':
      return 'Ready';
    case 'incoming':
      return 'Ringing';
    case 'calling':
      return 'Calling';
    case 'in_call':
      return 'In call';
    case 'error':
      return 'Error';
    case 'offline':
    default:
      return 'Offline';
  }
};

const getStatusClass = (status: VobizConnectionStatus) => {
  switch (status) {
    case 'registered':
      return 'bg-emerald-500';
    case 'incoming':
    case 'calling':
      return 'bg-amber-500 animate-pulse';
    case 'in_call':
      return 'bg-blue-500';
    case 'error':
      return 'bg-red-500';
    case 'connecting':
      return 'bg-yellow-500 animate-pulse';
    case 'offline':
    default:
      return 'bg-yellow-500';
  }
};

const loadVobizSdk = () => {
  if (window.Vobiz) return Promise.resolve();

  const existing = document.querySelector<HTMLScriptElement>(`script[src="${SDK_SRC}"]`);
  if (existing) {
    return new Promise<void>((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Vobiz WebRTC SDK')), { once: true });
    });
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SDK_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Vobiz WebRTC SDK'));
    document.head.appendChild(script);
  });
};

export const Softphone: React.FC<SoftphoneProps> = ({ agentId }) => {
  const [status, setStatus] = useState<VobizConnectionStatus>('offline');
  const [incomingCaller, setIncomingCaller] = useState<string | null>(null);
  const [incomingLeadInfo, setIncomingLeadInfo] = useState<{
    name?: string;
    lead_status?: string;
    tier_score?: number;
    assigned_to?: string;
    lead_id?: string;
  } | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [speakerBoost, setSpeakerBoost] = useState(2);
  const [error, setError] = useState<string | null>(null);
  const vobizRef = useRef<VobizInstance | null>(null);
  const audioElementRef = useRef<HTMLAudioElement>(null);
  const ringtoneRef = useRef<HTMLAudioElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const autoConnectAttemptedRef = useRef(false);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    if (status === 'in_call') {
      timer = setInterval(() => setCallDuration((prev) => prev + 1), 1000);
    } else {
      setCallDuration(0);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [status]);

  useEffect(() => {
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = speakerBoost;
    }
  }, [speakerBoost]);

  // Play ringtone when incoming call
  useEffect(() => {
    if (status === 'incoming' && ringtoneRef.current) {
      console.log('=== PLAYING RINGTONE ===');
      ringtoneRef.current.currentTime = 0;
      ringtoneRef.current.loop = true;
      ringtoneRef.current.play().catch(err => console.error('Failed to play ringtone:', err));
    } else if (status !== 'incoming' && ringtoneRef.current) {
      console.log('=== STOPPING RINGTONE ===');
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  }, [status]);

  const cleanupRemoteAudio = () => {
    sourceNodeRef.current?.disconnect();
    gainNodeRef.current?.disconnect();
    audioContextRef.current?.close().catch(() => undefined);
    sourceNodeRef.current = null;
    gainNodeRef.current = null;
    audioContextRef.current = null;
  };

  const playRemoteStream = (stream: MediaStream) => {
    const audioElement = audioElementRef.current;
    if (!audioElement) return;

    cleanupRemoteAudio();

    audioElement.srcObject = stream;
    audioElement.volume = 1;

    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();
      const sourceNode = audioContext.createMediaStreamSource(stream);
      const gainNode = audioContext.createGain();

      gainNode.gain.value = speakerBoost;
      sourceNode.connect(gainNode);
      gainNode.connect(audioContext.destination);

      audioContextRef.current = audioContext;
      sourceNodeRef.current = sourceNode;
      gainNodeRef.current = gainNode;
      audioElement.muted = true;
      return;
    } catch (err) {
      console.warn('Speaker boost unavailable, falling back to normal browser volume:', err);
      audioElement.muted = false;
      audioElement.play().catch(() => undefined);
    }
  };

  const attachRemoteAudio = () => {
    window.setTimeout(() => {
      const vobiz = vobizRef.current;
      if (!vobiz) return;

      const remoteView = vobiz.client.remoteView;
      const remoteStream = remoteView?.srcObject;
      if (remoteStream instanceof MediaStream) {
        playRemoteStream(remoteStream);
        return;
      }

      try {
        const pc = vobiz.client.getPeerConnection?.().pc;
        const audioTrack = pc?.getReceivers().find((receiver) => receiver.track?.kind === 'audio')?.track;
        if (audioTrack) {
          playRemoteStream(new MediaStream([audioTrack]));
        }
      } catch {
        // The SDK exposes remote audio differently across builds.
      }
    }, 1200);
  };

  const connect = async () => {
    console.log('=== SOFTPHONE CONNECT START ===');
    console.log('Username:', CREDENTIALS.username);
    
    try {
      setStatus('connecting');
      setError(null);
      await loadVobizSdk();

      if (!window.Vobiz) {
        throw new Error('Vobiz WebRTC SDK is unavailable.');
      }

      console.log('Vobiz SDK loaded, creating instance');
      const vobiz = new window.Vobiz({
        debug: 'ERROR',
        permOnClick: true,
        enableTracking: false,
        closeProtection: true,
        maxAverageBitrate: 48000,
      });

      vobiz.client.on('onWebrtcNotSupported', () => {
        console.error('WebRTC not supported');
        setStatus('error');
        setError('This browser does not support WebRTC calling.');
      });

      vobiz.client.on('onLogin', () => {
        console.log('=== SOFTPHONE LOGIN SUCCESS ===');
        setStatus('registered');
        setError(null);
      });

      vobiz.client.on('onLoginFailed', (reason?: string) => {
        console.error('=== SOFTPHONE LOGIN FAILED ===', reason);
        setStatus('error');
        setError(reason || 'Vobiz endpoint login failed.');
      });

      vobiz.client.on('onLogout', () => {
        console.log('=== SOFTPHONE LOGOUT ===');
        cleanupRemoteAudio();
        setStatus('offline');
        setIncomingCaller(null);
        setIsMuted(false);
      });

      vobiz.client.on('onIncomingCall', (callerName?: string) => {
        console.log('=== SOFTPHONE INCOMING CALL ===', callerName);
        setIncomingCaller(callerName || 'Unknown caller');
        setStatus('incoming');

        // Try to fetch lead info from queue based on phone number
        const fetchLeadInfo = async () => {
          try {
            const response = await fetch(`${API_BASE_URL}/api/call/queue/lead-by-phone?phone=${encodeURIComponent(callerName || '')}`);
            if (response.ok) {
              const data = await response.json();
              if (data.lead) {
                console.log('=== LEAD INFO FOUND ===', data.lead);
                setIncomingLeadInfo({
                  name: data.lead.name,
                  lead_status: data.lead.status,
                  tier_score: data.lead.tier_score,
                  assigned_to: data.lead.assigned_to,
                  lead_id: data.lead.id,
                });
              }
            }
          } catch (err) {
            console.error('Failed to fetch lead info:', err);
          }
        };

        if (callerName) {
          fetchLeadInfo();
        }
      });

      vobiz.client.on('onIncomingCallCanceled', () => {
        console.log('=== SOFTPHONE INCOMING CALL CANCELED ===');
        setIncomingCaller(null);
        setIncomingLeadInfo(null);
        setStatus('registered');
      });

      vobiz.client.on('onCallRemoteRinging', () => {
        console.log('=== SOFTPHONE CALL RINGING ===');
        setStatus('calling');
      });

      vobiz.client.on('onCallAnswered', () => {
        console.log('=== SOFTPHONE CALL ANSWERED ===');
        setIncomingCaller(null);
        setStatus('in_call');
        attachRemoteAudio();
      });

      vobiz.client.on('onCallTerminated', () => {
        console.log('=== SOFTPHONE CALL TERMINATED ===');
        cleanupRemoteAudio();
        setIncomingCaller(null);
        setIncomingLeadInfo(null);
        setStatus('registered');
        setIsMuted(false);
      });

      vobiz.client.on('onCallFailed', (callInfo?: { reason?: string }) => {
        console.error('=== SOFTPHONE CALL FAILED ===', callInfo);
        cleanupRemoteAudio();
        setIncomingCaller(null);
        setIncomingLeadInfo(null);
        setStatus('registered');
        setIsMuted(false);
        if (callInfo?.reason) setError(callInfo.reason);
      });

      vobiz.client.on('onMediaPermission', (granted?: boolean) => {
        console.log('=== SOFTPHONE MEDIA PERMISSION ===', granted);
        if (!granted) setError('Microphone permission is required to answer browser calls.');
      });

      vobizRef.current = vobiz;
      console.log('Attempting login with username:', CREDENTIALS.username);
      vobiz.client.login(CREDENTIALS.username, CREDENTIALS.password);
    } catch (err) {
      console.error('=== SOFTPHONE CONNECT ERROR ===', err);
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to connect Vobiz WebRTC.');
    }
  };

  const disconnect = () => {
    vobizRef.current?.client.logout();
    vobizRef.current = null;
    cleanupRemoteAudio();
    setStatus('offline');
    setIncomingCaller(null);
    setIncomingLeadInfo(null);
    setIsMuted(false);
  };

  const answerCall = () => {
    vobizRef.current?.client.answer();
    setStatus('in_call');
    setIncomingCaller(null);
    setIncomingLeadInfo(null);
    attachRemoteAudio();
  };

  const rejectCall = () => {
    vobizRef.current?.client.reject();
    setIncomingCaller(null);
    setIncomingLeadInfo(null);
    setStatus('registered');
  };

  const hangup = () => {
    vobizRef.current?.client.hangup();
    cleanupRemoteAudio();
    setStatus('registered');
    setIncomingLeadInfo(null);
    setIsMuted(false);
  };

  const toggleMute = () => {
    if (!vobizRef.current) return;
    if (isMuted) {
      vobizRef.current.client.unmute();
      setIsMuted(false);
    } else {
      vobizRef.current.client.mute();
      setIsMuted(true);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    console.log('=== SOFTPHONE AUTO-CONNECT CHECK ===');
    console.log('autoConnectAttempted:', autoConnectAttemptedRef.current);
    console.log('current status:', status);
    
    if (autoConnectAttemptedRef.current || status !== 'offline') {
      console.log('Skipping auto-connect');
      return;
    }

    console.log('Scheduling auto-connect in 800ms');
    autoConnectAttemptedRef.current = true;
    const timer = window.setTimeout(() => {
      console.log('Executing auto-connect');
      connect();
    }, 800);

    return () => {
      console.log('Clearing auto-connect timer');
      window.clearTimeout(timer);
    };
  }, [status]);

  return (
    <>
      <audio
        ref={ringtoneRef}
        src="https://edge-crm-worker.devadmin-27f.workers.dev/audio-host/ivr/greeting-business.wav"
        preload="auto"
      />

      <div className="fixed top-4 right-4 z-40 flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-lg backdrop-blur">
        <span className={`h-2.5 w-2.5 rounded-full ${getStatusClass(status)}`} />
        <span>{getStatusLabel(status)}</span>
      </div>

      {status === 'in_call' && (
        <div className="fixed bottom-20 right-4 z-30 w-[calc(100vw-2rem)] max-w-sm rounded-lg border border-slate-200 bg-white shadow-xl">
          <div className="space-y-3 px-4 py-3">
            <div className="space-y-3 rounded-md bg-blue-50 px-3 py-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-blue-900">Active call</span>
                <span className="font-mono text-blue-700">{formatTime(callDuration)}</span>
              </div>
              <label className="block text-xs font-semibold text-blue-900">
                Speaker boost {speakerBoost.toFixed(1)}x
                <input
                  type="range"
                  min="1"
                  max="4"
                  step="0.25"
                  value={speakerBoost}
                  onChange={(event) => setSpeakerBoost(Number(event.target.value))}
                  className="mt-1 w-full accent-blue-600"
                />
              </label>
            </div>

            <audio ref={audioElementRef} autoPlay playsInline />

            <div className="flex gap-2">
              <button
                type="button"
                onClick={toggleMute}
                className="flex-1 rounded-md bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
              >
                {isMuted ? 'Unmute' : 'Mute'}
              </button>
              <button
                type="button"
                onClick={hangup}
                className="flex-1 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Hang up
              </button>
            </div>
          </div>
        </div>
      )}

      {status === 'incoming' && incomingCaller && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-sm rounded-lg bg-white shadow-2xl">
            <div className="border-b border-slate-100 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="h-3 w-3 rounded-full bg-emerald-500 animate-pulse" />
                <h2 className="text-lg font-semibold text-slate-900">Incoming CRM call</h2>
              </div>
              <p className="mt-1 text-sm text-slate-500">Routed from IVR digit 4</p>
            </div>
            <div className="px-5 py-5">
              {incomingLeadInfo ? (
                <>
                  <p className="text-2xl font-bold text-slate-900">{incomingLeadInfo.name || 'Unknown'}</p>
                  <p className="mt-1 text-sm text-slate-500">{incomingCaller}</p>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-slate-600">Status:</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        incomingLeadInfo.lead_status === 'hot' ? 'bg-red-100 text-red-700' :
                        incomingLeadInfo.lead_status === 'warm' ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {incomingLeadInfo.lead_status || 'New'}
                      </span>
                    </div>
                    {incomingLeadInfo.tier_score && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-600">Tier:</span>
                        <span className="font-semibold">{incomingLeadInfo.tier_score}</span>
                      </div>
                    )}
                    {incomingLeadInfo.assigned_to && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-600">Assigned to:</span>
                        <span className="font-medium">{incomingLeadInfo.assigned_to}</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold text-slate-900">{incomingCaller}</p>
                  <p className="mt-1 text-sm text-slate-500">Loading lead information...</p>
                </>
              )}
              <p className="mt-3 text-sm text-slate-500">Answer here to talk from the CRM dashboard.</p>
              <div className="mt-5 flex gap-3">
                <button
                  type="button"
                  onClick={rejectCall}
                  className="flex-1 rounded-md bg-red-600 px-4 py-3 text-sm font-semibold text-white hover:bg-red-700"
                >
                  Decline
                </button>
                <button
                  type="button"
                  onClick={answerCall}
                  className="flex-1 rounded-md bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Answer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
